import { Field, Label, EXPERIMENTAL_Suggestion as Suggestion, type SuggestionItem } from "@digdir/designsystemet-react";
import { Fragment } from "react";
import type { GroupedSuggestionConfig } from "./groupedSuggestion";
import "./Filter.css";
import { FlexContainer } from "../../shared/FlexContainer";
import { InfoTooltip } from "../../shared/InfoTooltip";
import { MultipleSuggestionOptionContent } from "./MultipleSuggestionOptionContent";

type GroupedSuggestionFilterProps = {
    className?: string;
    label: string;
    selected: SuggestionItem[];
    onSelectedChange: (value: SuggestionItem[]) => void;
    config: GroupedSuggestionConfig;
    emptyText?: string;
    childOptionClassName?: string;
    standaloneFirstOptionClassName?: string;
    tooltipText?: string;
};

export function GroupedSuggestionFilter({
    className,
    label,
    selected,
    onSelectedChange,
    config,
    emptyText = "Ingen valg",
    childOptionClassName = "grouped-suggestion-filter-option-child",
    standaloneFirstOptionClassName = "grouped-suggestion-filter-option-standalone",
    tooltipText,
}: GroupedSuggestionFilterProps) {
    const selectedValues = new Set(selected.map((item) => item.value));

    return (
        <Field>
            <FlexContainer width="100%" gap="var(--ds-size-2)">
                <Label>{label}</Label>
                {tooltipText && <InfoTooltip text={tooltipText} withBg placement="top" />}
            </FlexContainer>
            <Suggestion className={className} multiple selected={selected} onSelectedChange={onSelectedChange}>
                <Suggestion.Input
                    aria-label={label}
                    placeholder={selected.length > 0 ? selected.map((item) => item.label).join(", ") : undefined}
                    className={selected.length > 0 ? "suggestion-input-has-selection" : undefined}
                />
                <Suggestion.Clear />
                <Suggestion.List>
                    <Suggestion.Empty>{emptyText}</Suggestion.Empty>

                    {config.groups.map((group) => (
                        <Fragment key={group.value}>
                            <Suggestion.Option value={group.value}>
                                <MultipleSuggestionOptionContent
                                    label={group.label}
                                    isSelected={selectedValues.has(group.value)}
                                />
                            </Suggestion.Option>
                            {group.children.map((child) => (
                                <Suggestion.Option
                                    key={child.value}
                                    value={child.value}
                                    className={childOptionClassName}
                                >
                                    <MultipleSuggestionOptionContent
                                        label={child.label}
                                        isSelected={selectedValues.has(child.value)}
                                    />
                                </Suggestion.Option>
                            ))}
                        </Fragment>
                    ))}

                    {(config.standaloneOptions ?? []).map((option, index) => (
                        <Suggestion.Option
                            key={option.value}
                            value={option.value}
                            className={index === 0 ? standaloneFirstOptionClassName : undefined}
                        >
                            <MultipleSuggestionOptionContent
                                label={option.label}
                                isSelected={selectedValues.has(option.value)}
                            />
                        </Suggestion.Option>
                    ))}
                </Suggestion.List>
            </Suggestion>
        </Field>
    );
}
